import React, { useEffect, useState } from 'react';

import * as Yup from 'yup';
import classNames from 'classnames';
import { ErrorMessage, Formik, FormikHelpers } from 'formik';
import { Button, Col, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { MyApplicationDetail } from '../../api/openapi';
import { RootState } from '../../store/rootReducer';
import { restValidationToForm, setYupLocale } from '../../utils/formUtils';
import { AppDispatch } from '../../store/store';
import { fetchUnions } from '../../store/createApplicationSlice';
import { applicationApi } from '../../api';

interface Props {
  item: MyApplicationDetail;
  onSaveSuccess: () => void;
}

interface Values {
  address: string;
  email: string;
  mobile: string;
  union: string;
  hasNoUnion: boolean;
  dontKnowUnion: boolean;
}

const ApplicationPersonEdit: React.FC<Props> = ({ item, onSaveSuccess }: Props) => {
  const { t, i18n } = useTranslation(['viewApplication', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const [validationSchema, setValidationSchema] = useState<Yup.AnySchema | null>(null);
  const { unions, unionsLoading, unionsError } = useSelector((state: RootState) => state.createApplication);

  useEffect(() => {
    dispatch(fetchUnions());
  }, [dispatch]);

  useEffect(() => {
    setYupLocale(Yup, i18n.language);
    setValidationSchema(
      Yup.object().shape({
        email: Yup.string().label(t('applicantEmail')).required(),
        mobile: Yup.string().label(t('applicantMobile')).required(),
        address: Yup.string().label(t('applicantAddress')),
      })
    );
  }, [t, i18n.language]);

  return (
    <>
      <Formik<Values>
        initialValues={{
          email: item.email,
          mobile: item.mobile,
          address: item.address || '',
          union: item.union?.id || '',
          hasNoUnion: !!item.hasNoUnion,
          dontKnowUnion: !!item.dontKnowUnion,
        }}
        validateOnBlur={false}
        // validateOnChange={false}
        validationSchema={validationSchema}
        // enableReinitialize
        onSubmit={async (values: Values, { setSubmitting, setFieldError }: FormikHelpers<Values>) => {
          try {
            await applicationApi.myApplicationPartialUpdate({
              uuid: item.id,
              patchedMyApplicationDetailRequest: {
                email: values.email,
                address: values.address,
                mobile: values.mobile,
                union:
                  !values.hasNoUnion && !values.dontKnowUnion && values.union
                    ? {
                        id: values.union,
                      }
                    : null,
                hasNoUnion: values.hasNoUnion,
                dontKnowUnion: values.dontKnowUnion,
              },
            });

            toast.success(t('applicationUpdate'));

            onSaveSuccess();
          } catch (e) {
            restValidationToForm({
              t,
              errorResponse: e.response,
              setFieldError,
              errorFieldMap: {
                name: 'name',
                ssn: 'ssn',
                address: 'address',
                email: 'email',
                mobile: 'mobile',
                union: 'union',
              },
            });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, touched, errors, getFieldProps, setFieldValue, setStatus, handleReset, handleSubmit }): React.ReactElement => (
          <Form
            noValidate
            onReset={handleReset}
            onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
              setStatus('submitted');
              handleSubmit(e);
            }}
          >
            <Form.Row>
              <Col md="4">
                <p className="small text-muted">{t('applicantHelp')}</p>
              </Col>
              <Col>
                <Form.Group controlId="name">
                  <Form.Label className="textBold">{t('name')}*:</Form.Label>
                  <Form.Text style={{ fontSize: '1rem' }}>{item.user.name}</Form.Text>
                </Form.Group>

                <Form.Group controlId="ssn">
                  <Form.Label className="textBold">{t('ssn')}*:</Form.Label>
                  <Form.Text style={{ fontSize: '1rem' }}>{item.user.username}</Form.Text>
                </Form.Group>

                <Form.Group controlId="address">
                  <Form.Label className="textBold">{t('address')}:</Form.Label>
                  <Form.Control
                    type="text"
                    className={classNames({
                      'is-invalid': touched.address && errors.address,
                    })}
                    {...getFieldProps('address')}
                  />
                  <Form.Text className="text-muted">{t('applicantAddress')}</Form.Text>
                </Form.Group>

                <Form.Group controlId="email">
                  <Form.Label className="textBold">{t('email')}*:</Form.Label>
                  <Form.Control
                    type="email"
                    className={classNames({
                      'is-invalid': touched.email && errors.email,
                    })}
                    {...getFieldProps('email')}
                  />
                  <Form.Text className="text-muted">{t('applicantEmail')}</Form.Text>
                  <ErrorMessage name="email" component="div" className="invalid-feedback d-block text-left" />
                </Form.Group>
                <Form.Group controlId="mobile">
                  <Form.Label className="textBold">{t('mobile')}*:</Form.Label>
                  <Form.Control
                    type="text"
                    className={classNames({
                      'is-invalid': touched.mobile && errors.mobile,
                    })}
                    {...getFieldProps('mobile')}
                  />
                  <Form.Text className="text-muted">{t('applicantMobile')}</Form.Text>
                  <ErrorMessage name="mobile" component="div" className="invalid-feedback d-block text-left" />
                </Form.Group>

                <Form.Group controlId="union">
                  <Form.Label className="textBold">{t('union')}:</Form.Label>

                  <Form.Row className="pb-2">
                    <Col md="2">
                      <Form.Check
                        custom
                        id="hasNoUnion"
                        name="hasNoUnion"
                        label={t('hasNoUnion')}
                        inline
                        type="checkbox"
                        checked={values.hasNoUnion === true}
                        onChange={(e) => {
                          setFieldValue('hasNoUnion', e.target.checked);
                          setFieldValue('dontKnowUnion', false);
                        }}
                      />
                    </Col>
                    <Col md="2">
                      <Form.Check
                        custom
                        id="dontKnowUnion"
                        name="dontKnowUnion"
                        label={t('dontKnowUnion')}
                        inline
                        type="checkbox"
                        checked={values.dontKnowUnion === true}
                        onChange={(e) => {
                          setFieldValue('dontKnowUnion', e.target.checked);
                          setFieldValue('hasNoUnion', false);
                        }}
                      />
                    </Col>
                  </Form.Row>
                  {!(values.dontKnowUnion || values.hasNoUnion) && (
                    <Form.Control
                      id="union"
                      as="select"
                      className={classNames({
                        'is-invalid': touched.union && errors.union,
                      })}
                      {...getFieldProps('union')}
                    >
                      <option value="">{t('chooseUnion')}</option>
                      {unions &&
                        unions.map((union) => (
                          <option key={union.id} value={union.id}>
                            {union.name}
                          </option>
                        ))}
                    </Form.Control>
                  )}
                  {unionsError && <div className="invalid-feedback d-block text-left">{t('common:kerfisvilla')}</div>}
                  {unionsLoading === 'idle' && unions?.length === 0 && <div className="invalid-feedback d-block text-left">{t('noUnions')}</div>}
                  <Form.Text className="text-muted">{unionsError === 'pending' && <FontAwesomeIcon spin icon={faSpinner} />}</Form.Text>
                  <ErrorMessage name="union" component="div" className="invalid-feedback d-block text-left" />
                </Form.Group>
                <hr />
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => {
                    setStatus('submitted');
                    handleSubmit();
                  }}
                >
                  {t('common:save')}
                </Button>
              </Col>
            </Form.Row>
          </Form>
        )}
      </Formik>
    </>
  );
};
export default ApplicationPersonEdit;
